/** The account view's reducers: unknown and partial positions are never flat; utilisation by integer arithmetic (UX04). */
import type {
  BalanceRow,
  ConcentrationRow,
  LimitRow,
  PositionRow,
} from "../../contracts/account.ts";
import {
  NOT_AVAILABLE,
  formatBasisPoints,
  formatDurationUs,
  formatQuantity,
  parseInstant,
} from "../../app/format.ts";
import { serverNowMicros, type Mono, type ServerClock } from "../../app/time.ts";

export interface PositionView {
  readonly row: PositionRow;
  /** The residual in words: a number, or `unknown` (never zero) when the fill is unknown. */
  readonly residualText: string;
  /** What the reader must not assume, in words; empty for a confirmed, open position. */
  readonly flags: readonly string[];
  /** Whether it still holds, or may hold, exposure. */
  readonly exposed: boolean;
  /** How old the mark is on the server's clock, in microseconds, or `null` without a mark. */
  readonly markAgeUs: number | null;
}

/** What to show for a position: unknown and partial fills are flagged and never read as flat (UX04). */
export function positionView(row: PositionRow, clock: ServerClock, mono: Mono): PositionView {
  const flags: string[] = [];
  if (row.fillState === "UNKNOWN") {
    flags.push(
      "Fill unknown: the quantity is not confirmed, so this is not a closed or flat position.",
    );
  }
  if (row.fillState === "PARTIAL") {
    flags.push(
      `Partially filled: ${formatQuantity(row.acquiredQuantity)} acquired so far, not the full order.`,
    );
  }
  if (row.protection === "UNPROTECTED" || row.protection === "UNKNOWN") {
    if (row.status !== "CLOSED") {
      flags.push(`Protection is ${row.protection.toLowerCase()}.`);
    }
  } else if (row.protection === "PARTIAL") {
    flags.push("Only part of the residual is protected.");
  }
  if (row.source === "SIMULATED") {
    flags.push("Simulated execution: shown apart from observed fills, never merged with them.");
  }
  const marked =
    row.markAt === null ? null : Number(serverNowMicros(clock, mono) - parseInstant(row.markAt));
  return {
    row,
    residualText: row.residualQuantity === null ? "unknown" : formatQuantity(row.residualQuantity),
    flags,
    exposed: row.status !== "CLOSED",
    markAgeUs: marked === null ? null : Math.max(0, marked),
  };
}

/** Mark age in words, or a dash for no mark (never zero). */
export function markAgeText(view: PositionView): string {
  return view.markAgeUs === null ? NOT_AVAILABLE : formatDurationUs(view.markAgeUs);
}

/** Used over limit in basis points by integer arithmetic; `null` when the limit is zero. */
export function utilisationBp(used: bigint, limit: bigint): number | null {
  return limit === 0n ? null : Number((used * 10_000n) / limit);
}

export interface Utilisation {
  readonly text: string;
  /** Over the limit: said in words as well as shown. */
  readonly over: boolean;
}

/** Utilisation in words: a percentage, `no limit set` for a zero limit, flagged when over. */
export function utilisationOf(used: bigint, limit: bigint): Utilisation {
  const bp = utilisationBp(used, limit);
  if (bp === null) {
    return { text: "no limit set", over: used > 0n };
  }
  return { text: formatBasisPoints(bp), over: used > limit };
}

/** Group exposure rows by dimension, largest utilisation first within each. */
export function groupExposure(rows: readonly ConcentrationRow[]): readonly {
  readonly dimension: ConcentrationRow["dimension"];
  readonly rows: ConcentrationRow[];
}[] {
  const order = ["NAME", "SECTOR", "CATALYST"] as const;
  return order
    .map((dimension) => ({
      dimension,
      rows: rows
        .filter((row) => row.dimension === dimension)
        .sort((a, b) =>
          a.exposurePaise === b.exposurePaise ? 0 : a.exposurePaise < b.exposurePaise ? 1 : -1,
        ),
    }))
    .filter((group) => group.rows.length > 0);
}

/** Realised outcomes kept separate by provenance (U039): observed and simulated are never summed. */
export function outcomeLines(balance: BalanceRow): readonly (readonly [string, bigint | null])[] {
  return [
    ["Realised, observed executions", balance.realisedObservedPaise],
    ["Realised, simulated executions", balance.realisedSimulatedPaise],
    ["Marked (open positions)", balance.markedPaise],
    ["Stress scenario", balance.stressPaise],
  ];
}

/** The limits that block an action now. */
export function blockingLimits(limits: readonly LimitRow[]): readonly LimitRow[] {
  return limits.filter((limit) => limit.blocking);
}

/** The count of positions by provenance, for the observed-versus-simulated line (U039, U040). */
export function provenanceCounts(
  rows: readonly PositionRow[],
): Record<PositionRow["source"], number> {
  const counts: Record<PositionRow["source"], number> = {
    BROKER_REPORTED: 0,
    MANUAL: 0,
    SIMULATED: 0,
  };
  for (const row of rows) {
    counts[row.source] += 1;
  }
  return counts;
}
